import { AppFactory } from 'vue-infra';

import BaseService from '../../common/BaseService';
import MedalliaClient from '../../clients/MedalliaClient';
import MedalliaTransformer from './medalliaTransformer';

const store = AppFactory.store;

const failedInitializationObj = {
  tpbToAdapterClientId: 'Initialization failed',
  tpbToAdapterClientSecret: 'Initialization failed',
  adapterEndpoint: 'Initialization failed',
  sentinelEndpoint: 'Initialization failed',
  sentinelConsumerID: 'Initialization failed',
  sentinelConsumerSecret: 'Initialization failed',
  accountId: null,
};

class MedalliaService extends BaseService {
  constructor() {
    super({ name: 'MedalliaService' });
    this.newMedalliaClient = null;
  }

  async testMedalliaAPIConfig(medalliaTestAPIConfig) {
    this.newMedalliaAPIConfigClient = medalliaTestAPIConfig.action;
    const medalliaConfigSettings = MedalliaTransformer.transformMedalliaApiConnectionRequest(medalliaTestAPIConfig);
    let medalliaAPIConfigResponse;
    try {
      this.newMedalliaAPIConfigClient = createMedalliaTestAPIConfigClient(medalliaTestAPIConfig);
      medalliaAPIConfigResponse = await this.sendClientRequest({
        client: this.newMedalliaAPIConfigClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaConfig',
      });
      medalliaAPIConfigResponse = MedalliaTransformer.transformMedalliaTestAPIConfigResponse(medalliaAPIConfigResponse);
    } catch (error) {
      medalliaAPIConfigResponse = Promise.reject(error);
      this.logError(`Error testing medallia API config: ${error}`);
    }
    return medalliaAPIConfigResponse;
  }

  async saveConfigToDB(medalliaConfig) {
    this.newMedalliaConfigClient = medalliaConfig.action;
    const medalliaConfigSettings = MedalliaTransformer.transformMedalliaSaveConfigRequest(medalliaConfig);
    let medalliaConfigResponse;
    if (medalliaConfigSettings.method === MedalliaClient.HTTP_METHODS.PATCH) {
      this.newMedalliaConfigClient = createMedalliaPatchConfigClient(medalliaConfigSettings);
    } else {
      this.newMedalliaConfigClient = createMedalliaSaveConfigClient(medalliaConfigSettings);
    }
    try {
      medalliaConfigResponse = await this.sendClientRequest({
        client: this.newMedalliaConfigClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaSaveConfig',
      });
      medalliaConfigResponse = MedalliaTransformer.transformMedalliaConfigResponse(medalliaConfigResponse);
    } catch (error) {
      medalliaConfigResponse = Promise.reject(error);
      this.logError(`Error saving config: ${error}`);
    }
    return medalliaConfigResponse;
  }

  async setSkillSurveyMappingsConfig(medalliaMappingConfig) {
    this.newMedalliaMappingConfigClient = medalliaMappingConfig.action;
    const medalliaConfigSettings = MedalliaTransformer.transformMedalliaMappingConnectionRequest(medalliaMappingConfig);
    let medalliaMappingResponse;
    try {
      this.newMedalliaMappingClient = createMedalliaMappingClient(medalliaMappingConfig);
      medalliaMappingResponse = await this.sendClientRequest({
        client: this.newMedalliaMappingClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaConfig',
      });
      medalliaMappingResponse = MedalliaTransformer.transformMedalliaMappingConnectionResponse(medalliaMappingResponse);
    } catch (error) {
      medalliaMappingResponse = Promise.reject(error);
      this.logError(`Error Attempting To Map: ${error}`);
    }
    return medalliaMappingResponse;
  }

  async setSurveySkillsToDB(medalliaSurveySkillsToDBConfig) {
    this.newMedalliaSurveySkillFromDBClient = medalliaSurveySkillsToDBConfig.action;
    const medalliaConfigSettings = MedalliaTransformer.transformMedalliaSurveySkillsToDBRequest(medalliaSurveySkillsToDBConfig);
    let medalliaSurveySkillsToDBResponse;
    try {
      this.newMedalliaSurveySkillFromDBClient = createMedalliaSurveySkillsToDBClient(medalliaSurveySkillsToDBConfig);
      medalliaSurveySkillsToDBResponse = await this.sendClientRequest({
        client: this.newMedalliaSurveySkillFromDBClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaConfig',
      });
      medalliaSurveySkillsToDBResponse = MedalliaTransformer.transformMedalliaSurveySkillsToDBResponse(medalliaSurveySkillsToDBResponse);
    } catch (error) {
      medalliaSurveySkillsToDBResponse = Promise.reject(error);
      this.logError(`Error Attempting to map: ${error}`);
    }
    return medalliaSurveySkillsToDBResponse;
  }

  async delSurveySkillFromDB(medalliaDelSurveySkillFromDBConfig) {
    this.newMedalliaDelSurveySkillFromDBClient = medalliaDelSurveySkillFromDBConfig.action;
    const medalliaConfigSettings = MedalliaTransformer.transformMedalliaDelSurveySkillFromDBRequest(medalliaDelSurveySkillFromDBConfig);
    let medalliaDelSurveySkillFromDBResponse;
    try {
      this.newMedalliaDelSurveySkillFromDBClient = createMedalliaSurveySkillFromDBClient(medalliaDelSurveySkillFromDBConfig);
      medalliaDelSurveySkillFromDBResponse = await this.sendClientRequest({
        client: this.newMedalliaDelSurveySkillFromDBClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaConfig',
      });
      medalliaDelSurveySkillFromDBResponse = MedalliaTransformer.transformMedalliaDelSurveySkillFromDBResponse(medalliaDelSurveySkillFromDBResponse);
    } catch (error) {
      medalliaDelSurveySkillFromDBResponse = Promise.reject(error);
      this.logError(`Error Attempting to map: ${error}`);
    }
    return medalliaDelSurveySkillFromDBResponse;
  }

  async fetchMedalliaInitializationInfo(medalliaInitInfo) {
    this.newMedalliaClient = medalliaInitInfo;
    const accountId = await store.getters['session/getValue']('accountId');
    const medalliaConfigSettings = {
      method: MedalliaClient.HTTP_METHODS.POST,
    };
    let medalliaInitializationInfoResponse = {};
    try {
      this.medalliaInitializationInfoClient = createMedalliaInitializationInfoClient(medalliaInitInfo);
      medalliaInitializationInfoResponse = await this.sendClientRequest({
        client: this.medalliaInitializationInfoClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaConfig',
      });
      medalliaInitializationInfoResponse = MedalliaTransformer.transformMedalliaInitializationInfoResponse(medalliaInitializationInfoResponse);
    } catch (error) {
      medalliaInitializationInfoResponse = Promise.reject(error);
      this.logError(`Error fetching medalliaConfigResponse: ${error}`);
      medalliaInitializationInfoResponse = failedInitializationObj;
    }
    medalliaInitializationInfoResponse.accountId = accountId;
    return medalliaInitializationInfoResponse;
  }

  async fetchMedalliaAccountInfo(medalliaAccountInfo) {
    this.newMedalliaClient = medalliaAccountInfo;
    const medalliaConfigSettings = {
      method: MedalliaClient.HTTP_METHODS.GET,
    };
    let medalliaAccountInfoResponse;
    try {
      this.medalliaAccountInfoClient = createMedalliaAccountInfoClient(medalliaAccountInfo);
      medalliaAccountInfoResponse = await this.sendClientRequest({
        client: this.medalliaAccountInfoClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaAccountConfig',
      });
      medalliaAccountInfoResponse = MedalliaTransformer.transformMedalliaAccountInfoResponse(medalliaAccountInfoResponse);
    } catch (error) {
      medalliaAccountInfoResponse = Promise.reject(error);
      this.logError(`Error fetching medalliaAccountConfigResponse: ${error}`);
    }
    return medalliaAccountInfoResponse;
  }

  async getSurveys(medalliaAllSurvey) {
    this.newMedalliaClient = medalliaAllSurvey;
    const medalliaConfigSettings = {
      method: MedalliaClient.HTTP_METHODS.GET,
    };
    let medalliaAllSurveyResponse;
    try {
      this.medalliaAllSurveyClient = createMedalliaAllSurveyClient(medalliaAllSurvey);
      medalliaAllSurveyResponse = await this.sendClientRequest({
        client: this.medalliaAllSurveyClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaConfig',
      });
      medalliaAllSurveyResponse = MedalliaTransformer.transformMedalliaAllSurveyResponse(medalliaAllSurveyResponse);
    } catch (error) {
      medalliaAllSurveyResponse = Promise.reject(error);
      this.logError(`Error fetching medalliaConfigResponse: ${error}`);
    }
    return medalliaAllSurveyResponse;
  }

  async getSkills(medalliaAllSkill) {
    this.newMedalliaClient = medalliaAllSkill;
    const medalliaConfigSettings = {
      method: MedalliaClient.HTTP_METHODS.GET,
    };
    let medalliaAllSkillResponse;
    try {
      this.medalliaAllSkillClient = createMedalliaAllSkillClient(medalliaAllSkill);
      medalliaAllSkillResponse = await this.sendClientRequest({
        client: this.medalliaAllSkillClient,
        settings: medalliaConfigSettings,
        errorMsg: 'Could\'t fetch medalliaConfig',
      });
      medalliaAllSkillResponse = MedalliaTransformer.transformMedalliaAllSkillResponse(medalliaAllSkillResponse);
    } catch (error) {
      medalliaAllSkillResponse = Promise.reject(error);
      this.logError(`Error fetching medalliaConfigResponse: ${error}`);
    }
    return medalliaAllSkillResponse;
  }
}

// Client Creation Below

function createMedalliaInitializationInfoClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_INITIALIZATION_INFO,
    contextName: 'GetMedalliaInitializationInfo',
    medalliaConfig,
  });
}

function createMedalliaAccountInfoClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_ACCOUNT_INFO,
    contextName: 'GetMedalliaAccountInfo',
    medalliaConfig,
  });
}

function createMedalliaAllSurveyClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_ALL_SURVEYS,
    contextName: 'GetSurveys',
    medalliaConfig,
  });
}

function createMedalliaAllSkillClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_ALL_SKILLS,
    contextName: 'GetSkills',
    medalliaConfig,
  });
}

function createMedalliaTestAPIConfigClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_TEST_API_CONFIG,
    contextName: 'TestAPIConfig',
    medalliaConfig,
  });
}

function createMedalliaSaveConfigClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_SAVE_CONFIG_TO_DB,
    contextName: 'SaveConfigToDB',
    medalliaConfig,
  });
}

function createMedalliaPatchConfigClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_PATCH_CONFIG_TO_DB,
    contextName: 'PatchConfigToDB',
    medalliaConfig,
  });
}

function createMedalliaMappingClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_MAPPING_CONFIG,
    contextName: 'MappingConfig',
    medalliaConfig,
  });
}

function createMedalliaSurveySkillsToDBClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_SURVEY_SKILLS_TO_DB,
    contextName: 'SurveySkillsToDB',
    medalliaConfig,
  });
}

function createMedalliaSurveySkillFromDBClient(medalliaConfig) {
  return new MedalliaClient({
    resource: MedalliaClient.RESOURCES.MEDALLIA_DEL_SURVEY_SKILL_FROM_DB,
    contextName: 'SurveySkillsToDB',
    medalliaConfig,
  });
}

const medalliaService = new MedalliaService();

export default medalliaService;
